import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldMark } from "../../components/onix/ShieldMark";
import { adminAxios, formatDetail } from "../../lib/admin";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("credentials");
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Administración | ONIX";
    adminAxios
      .get("/auth/me")
      .then(() => navigate("/admin/panel"))
      .catch(() => {});
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await adminAxios.post("/auth/login", { email: email.trim(), password });
      if (data.requires_2fa) {
        setMfaToken(data.mfa_token);
        setStep("code");
        return;
      }
      navigate("/admin/panel");
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || "No se pudo iniciar sesión.");
    } finally {
      setLoading(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await adminAxios.post("/auth/2fa/verify", { mfa_token: mfaToken, code: code.trim() });
      navigate("/admin/panel");
    } catch (err) {
      const status = err?.response?.status;
      const msg = formatDetail(err?.response?.data?.detail) || "No se pudo verificar el código.";
      setError(msg);
      if (status === 401 && msg.includes("expirado")) {
        setStep("credentials");
        setMfaToken("");
        setCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-screen bg-onix-black flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-10">
          <ShieldMark variant="white" className="w-[110px]" eager alt="Escudo ONIX" />
          <p className="tech-label mt-6">ADMINISTRACIÓN · ACCESO RESTRINGIDO</p>
        </div>

        {step === "credentials" && (
          <form data-testid="admin-login-form" onSubmit={submit} className="border border-onix-line bg-onix-black2 p-8 space-y-6">
            <h1 className="font-serif font-bold text-2xl text-onix-white">Acceso de administrador</h1>
            <div>
              <label htmlFor="admin-email" className="block text-[11px] font-bold uppercase tracking-[0.14em] text-onix-silver mb-2">
                Email
              </label>
              <input
                data-testid="admin-email"
                id="admin-email"
                type="email"
                className="onix-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block text-[11px] font-bold uppercase tracking-[0.14em] text-onix-silver mb-2">
                Contraseña
              </label>
              <input
                data-testid="admin-password"
                id="admin-password"
                type="password"
                className="onix-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" role="alert" className="text-xs font-bold text-onix-white border border-dashed border-onix-silver p-3">
                {error}
              </p>
            )}
            <button data-testid="admin-login-submit" type="submit" disabled={loading} className="btn-onix-primary w-full disabled:opacity-60">
              {loading ? "Accediendo…" : "Entrar"}
            </button>
          </form>
        )}

        {step === "code" && (
          <form data-testid="admin-2fa-form" onSubmit={submitCode} className="border border-onix-line bg-onix-black2 p-8 space-y-6">
            <h1 className="font-serif font-bold text-2xl text-onix-white">Verificación en dos pasos</h1>
            <p className="text-sm text-onix-silver leading-relaxed">
              Introduce el código de 6 dígitos de tu app de autenticación o un código de recuperación.
            </p>
            <div>
              <label htmlFor="admin-2fa-code" className="block text-[11px] font-bold uppercase tracking-[0.14em] text-onix-silver mb-2">
                Código
              </label>
              <input
                data-testid="admin-2fa-code"
                id="admin-2fa-code"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                className="onix-input tracking-[0.3em]"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={20}
                autoFocus
                required
              />
            </div>
            {error && (
              <p data-testid="admin-2fa-error" role="alert" className="text-xs font-bold text-onix-white border border-dashed border-onix-silver p-3">
                {error}
              </p>
            )}
            <button data-testid="admin-2fa-submit" type="submit" disabled={loading || code.trim().length < 6} className="btn-onix-primary w-full disabled:opacity-60">
              {loading ? "Verificando…" : "Verificar"}
            </button>
            <button
              data-testid="admin-2fa-back"
              type="button"
              className="w-full text-[11px] font-bold uppercase tracking-[0.14em] text-onix-gray hover:text-onix-white transition-colors"
              onClick={() => {
                setStep("credentials");
                setMfaToken("");
                setCode("");
                setError("");
              }}
            >
              Volver al inicio de sesión
            </button>
          </form>
        )}

        <p className="tech-label text-center mt-8">ONIX · INTELLIGENCE & CYBERSECURITY SYSTEMS</p>
      </div>
    </main>
  );
}
